import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { ActionEnum, CallStatusEnum, IAdminViewModel, IAPICallResult, ICultureInfo, IGenericList, IGetDynListParams, ILoginResult, ILoginViewModel, IMenuTab, IRegExpResultModel, ISaveConfigModel, IUploadImgResult, IViewContext, IViewContextParams, MessageLevelEnum, IRefreshTokenResult, IRunX3WSResult, IPoolInfo, IPoolActionResult, IX3AdminViewModel, IPoolMappingEntry } from '../abstraction';
import { AppLabels } from '../core/AppLabels';
import { KEvent } from '../core/utils';
import { Karma, securitySvc } from '../Karma';

const headers = {
	'Content-Type': 'application/json',
	'Access-Control-Allow-Origin': '*',
}

export class Apis {
	private baseUrl: string = "";

	static URL_login = "/apis/login";
	static URL_GetUserData = "/apis/log";
	static URL_logOut = "/apis/logout";
	static URL_getMenu = "/apis/getappmenu";
	static URL_getCultures = "/apis/getCultures";
	static URL_saveConfig = "/apis/saveConfig";
	static URL_getappLabels = "/apis/getapplabels";
	static URL_getItemDetail = "/apis/items/getItemDetail";
	static URL_setLoginlabels = "/apis/setloginlabels";
	static URL_getLoginresources = "/apis/getLoginResources";
	static URL_getAdminViewModel = "/apis/getAdminViewModel";
	static URL_getX3LangCode = "/apis/getX3LangCode";

	static URL_getViewContext = "/apis/getViewContext";
	static URL_getGenericList = "/apis/getGenericList";
	static URL_getDynamicList = "/apis/getDynamicList";
	static URL_refreshToken = "/apis/RefreshToken";
	static URL_getLogList = "/apis/getLogList";
	static URL_getLogFile = "/apis/getLogFile";
	static URL_RegParser = "/apis/regparser";
	static URL_UploadImg = "/apis/uploadImg";
	static URL_RunX3WS = "/apis/runX3WS";
	static URL_GetPoolList = "/apis/loadPoolList";
	static URL_StartPool = "/apis/startPool";
	static URL_StopPool = "/apis/stopPool";
	static URL_SavePoolConfig = "/apis/savePoolCfg";
	static URL_GetX3AdminViewModel = "/apis/GetX3AdminViewModel";
	


	constructor() {
		if (process.env.NODE_ENV === 'production') {
			this.baseUrl = window.location.protocol + "//" + window.location.host ;
		}
		else {
			this.baseUrl = "";
		}
	}

	// Méthode proxy gérant les appels au serveur d'API
	// 
	private async Post<T>(url: string, data: any): Promise<IAPICallResult<T>> {
		console.debug("Post : " + this.baseUrl + url);

		let cfg: AxiosRequestConfig = {
			withCredentials: false,
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': '*',
				'Authorization': ''
			}
		};

		if (securitySvc != null)
			if (securitySvc.currentUser != null) {
				cfg.headers["Authorization"] = securitySvc.currentUser.authString;
			}

		let postResult: AxiosResponse;

		try {
			postResult = await axios.post(this.baseUrl + url, data, cfg);
		}
		catch (err:any) {
			console.log(err)

			if (err.response.status == CallStatusEnum.ServerUnauthorized) {
				return <IAPICallResult<T>>{
					action: ActionEnum.None,
					status: CallStatusEnum.ServerGenericError,
					data: null,
					url: "/",
					sound: "",
					messages: [{ level: MessageLevelEnum.Information, message: AppLabels.sessionTimeOutError }],
					focus: ""
				};
			}

			return <IAPICallResult<T>>{
				action: ActionEnum.None,
				status: CallStatusEnum.ServerGenericError,
				data: null,
				url: "/",
				sound: "",
				messages: [{ level: MessageLevelEnum.Critical, message: AppLabels.criticalError }],
				focus:""
			};
		}

		// HTTP OK
		// Récupère le contenu du message
		if (postResult.status == CallStatusEnum.ServerHTTPOK) {

			let result: IAPICallResult<T> = postResult.data;
			// Temporary redirect => back to login
			if (result.status == CallStatusEnum.ServerTempRedirect) {
				result.url = "/";
				return result;
			}

			this.OnAPIRequestCompleted.trigger(result);
			return result;
		}

		let result: IAPICallResult<T> = {
			status: postResult.status,
			messages: [],
			action: ActionEnum.None,
			focus: "",
			sound: "",
			data: null,
			url: ""
		}

		result.messages.push({ level: MessageLevelEnum.Information, message: postResult.statusText });

		this.OnAPIRequestCompleted.trigger(result);

		return result;
	}

	private _OnPostCompleted: KEvent<IAPICallResult<any>> = new KEvent<IAPICallResult<any>>();
	public get OnAPIRequestCompleted(): KEvent<IAPICallResult<any>> { return this._OnPostCompleted; }

	private async Get<T>(url: string) {
		console.log("Get : " + this.baseUrl + url);

		let cfg: AxiosRequestConfig = {
			withCredentials: false,
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': '*',
				'Authorization': ''
			}
		};

		if (securitySvc != null)
			if (securitySvc.currentUser != null) {
				cfg.headers["Authorization"] = securitySvc.currentUser.authString;
			}

		let result = await axios.get(this.baseUrl + url, cfg);

		let r: IAPICallResult<T> = result.data;

		this.OnAPIRequestCompleted.trigger(r);
		return r;
	}

	// Lecture des informations sur les différentes cultures disponibles
	//
	public async getCultures(): Promise<IAPICallResult<ICultureInfo[]>> {
		return await this.Get<ICultureInfo[]>(Apis.URL_getCultures);
	}

	// Retourne le code de la langue X3 selon la culture passée
	public async getX3LangCode(lang?: string): Promise<IAPICallResult<string>> {
		try {
			return await this.Post<string>(Apis.URL_getX3LangCode, { lang: lang });
		}
		catch (err) {
			console.error(err)
		}
	}

	// Retourne les libellés de l'application
	public async getLoginResources(lang?: string): Promise<IAPICallResult<ILoginViewModel>> {
		return await this.Post<ILoginViewModel>(Apis.URL_getLoginresources, { lang: lang });
	}

	//#region Authentification

	public async postLogin(login: string, password: string): Promise<IAPICallResult<ILoginResult>> {
		let postResult: AxiosResponse<IAPICallResult<ILoginResult>> = await axios.post(this.baseUrl + Apis.URL_login, { 'username': login, 'password': password }, { headers: headers });
		if (postResult.status == CallStatusEnum.ServerHTTPOK) postResult.status = postResult.data.status;
		
		return postResult.data;
	}

	// Retourne les informations liées à l'utilisateur connecté (identité, banques de ressources...)
	public async getUserData(id: string): Promise<IAPICallResult<ILoginResult>> {
		let cfg: AxiosRequestConfig = {
			withCredentials: false,
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': '*',
				'Authorization': ''
			}
		};

		if (securitySvc != null)
			if (securitySvc.currentUser != null) {
				cfg.headers["Authorization"] = securitySvc.currentUser.authString;
			}

		let postResult: AxiosResponse<IAPICallResult<ILoginResult>> = await axios.post(this.baseUrl + Apis.URL_GetUserData, { 'id': id }, cfg);
		return postResult.data;
	}

	// Provoque la déconexion de l'utilisateur
	public async logOut(notrace: boolean = false): Promise<IAPICallResult<boolean>> {
		return await this.Post<boolean>(Apis.URL_logOut, { notrace: notrace }); //
	}

	// Prolonge la durée de vie du token
	public async refreshToken(): Promise<IAPICallResult<IRefreshTokenResult>> {
		return await this.Post<IRefreshTokenResult>(Apis.URL_refreshToken, null);
	}

	//#endregion

	// Lecture d'un liste générique
	public async getGenericList<T>(code: string): Promise<IAPICallResult<IGenericList>> {
		return await this.Post<IGenericList>(Apis.URL_getGenericList, { code: code });
	}

	// Lecture d'une liste avec paramètres (contexte)
	public async getDynamicList<T>(viewstate: IGetDynListParams): Promise<IAPICallResult<IGenericList>> {
		return await this.Post<IGenericList>(Apis.URL_getDynamicList, viewstate);
	}

	// Obtient le contexte complet d'une page
	public async getViewContext<T>(viewstate: IViewContextParams): Promise<IAPICallResult<IViewContext>> {
		return await this.Post<IViewContext>(Apis.URL_getViewContext, viewstate);
	}

	// Général 
	public async getUserMenus(menCode: string): Promise<IAPICallResult<IMenuTab[]>> {
		var lang = "";
		var culture = Karma.getCurrentCulture();
		if (culture) lang = culture.code;

		return await this.Post<IMenuTab[]>(Apis.URL_getMenu, { CODE: menCode, LANG: lang });
	}

	public async saveConfig(config: ISaveConfigModel): Promise<IAPICallResult<boolean>> {
		return await this.Post<boolean>(Apis.URL_saveConfig, { appconfig: config });
	}

	// Obtient le viewmodel de la page de configuration
	public async getAdminViewModel(): Promise<IAPICallResult<IAdminViewModel>> {
		return await this.Post<IAdminViewModel>(Apis.URL_getAdminViewModel, {});
	}

	public async getLogList(): Promise<IAPICallResult<string[]>> {
		return await this.Post<string[]>(Apis.URL_getLogList, {});
	}

	public async getLogFile(file: string): Promise<IAPICallResult<string[]>> {
		return await this.Post<string[]>(Apis.URL_getLogFile, {file:file});
	}

	public async regParser(expression: string, value: string): Promise<IAPICallResult<IRegExpResultModel>> {
		return await this.Post<IRegExpResultModel>(Apis.URL_RegParser, { expression: expression, value: value });
	}

	public async uploadImg(data: string): Promise<IAPICallResult<IUploadImgResult>> {
		return await this.Post<IUploadImgResult>(Apis.URL_UploadImg, { data: data });
	}

	public async runX3WS(webService: string, callParams: string, pool: string): Promise<IAPICallResult<IRunX3WSResult>> {
		return await this.Post<IRunX3WSResult>(Apis.URL_RunX3WS, { wsName: webService, wsParams: callParams, wsPool: pool });
	}

	public async getPoolList(): Promise<IAPICallResult<IPoolInfo[]>> {
		return await this.Post<IPoolInfo[]>(Apis.URL_GetPoolList, { file: "" });
	}

	public async startPool(poolId: string): Promise<IAPICallResult<IPoolActionResult>> {
		return await this.Post<IPoolActionResult>(Apis.URL_StartPool, { poolId: poolId });
	}

	public async stopPool(poolId: string): Promise<IAPICallResult<IPoolActionResult>> {
		return await this.Post<IPoolActionResult>(Apis.URL_StopPool, { poolId: poolId });
	}

	public async savePoolConfig(mapping: IPoolMappingEntry[], pools: string[]): Promise<IAPICallResult<boolean>> {
		return await this.Post<boolean>(Apis.URL_SavePoolConfig, { poolMapping: mapping, pools:pools });
	}

	public async getX3AdminViewModel(): Promise<IAPICallResult<IX3AdminViewModel>> {
		return await this.Post<IX3AdminViewModel>(Apis.URL_GetX3AdminViewModel, { });
	}	
}

